import { useState } from "react";
import { usePropertySearch } from "@/hooks/usePropertySearch";
import PropertySearchHeader from "@/components/buyer/search/PropertySearchHeader";
import PropertySearchResults from "@/components/buyer/search/PropertySearchResults";
import PropertyMapView from "@/components/buyer/map/PropertyMapView";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const SellerPropertySearchPage = () => {
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  
  const {
    searchFilters,
    setSearchFilters,
    properties,
    isLoading,
    hasSearched,
    handleSearchWithFilters,
    clearFilters,
    updateLocation,
    resultsTruncated,
    totalCount
  } = usePropertySearch();

  const handleSearch = () => {
    handleSearchWithFilters();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="space-y-6">
      <PropertySearchHeader
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filters={searchFilters}
        onFiltersChange={setSearchFilters}
        onApplyFilters={handleSearch}
        onClearFilters={clearFilters}
      />

      {/* Simple Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter city, state, or zip code..."
              value={searchFilters.location}
              onChange={(e) => updateLocation(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={isLoading}>
              <Search className="h-4 w-4 mr-2" />
              {isLoading ? "Searching..." : "Search"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results or Empty State with Input Criteria Prompt */}
      {hasSearched ? (
        <PropertySearchResults
          properties={properties}
          viewMode={viewMode}
          searchLocation={searchFilters.location}
          onClearFilters={clearFilters}
          isSearchResults={true}
          resultsTruncated={resultsTruncated}
          totalCount={totalCount}
        />
      ) : viewMode === "map" ? (
        <div className="relative">
          <PropertyMapView 
            properties={[]} 
            searchLocation="" 
          />
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center pointer-events-none">
            <Card className="pointer-events-auto">
              <CardContent className="flex flex-col items-center justify-center py-8 px-12 text-center">
                <Search className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Input Search Criteria</h3>
                <p className="text-muted-foreground max-w-md">
                  Enter a location or use the filters above to search for properties on the platform.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Input Search Criteria</h3>
            <p className="text-muted-foreground max-w-md">
              Enter a location or use the filters above to search for properties on the platform.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SellerPropertySearchPage;
